var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import { ChevronLeft, ChevronRight } from '@sumup/icons';
import { useClickTrigger } from '@sumup/collector';
import styled from '../../styles/styled';
import IconButton from '../IconButton';
import { AccessibilityError } from '../../util/errors';
import { PageSelect } from './components/PageSelect';
import { PageList } from './components/PageList';
import * as PaginationService from './PaginationService';
const Nav = styled.nav `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: ${(p) => p.theme.spacings.kilo};
`;
const prevButtonStyles = (theme) => css `
    margin-right: ${theme.spacings.kilo};
    flex-shrink: 0;
  `;
const nextButtonStyles = (theme) => css `
    margin-left: ${theme.spacings.kilo};
    flex-shrink: 0;
  `;
/**
 * Pagination is a component to show pages numbers calculate dynamically.
 */
export const Pagination = (_a) => {
    var { currentPage = 1, totalPages, onChange, label, previousLabel, nextLabel, pageLabel = (page) => `Go to page ${page}`, totalLabel, tracking = {} } = _a, props = __rest(_a, ["currentPage", "totalPages", "onChange", "label", "previousLabel", "nextLabel", "pageLabel", "totalLabel", "tracking"]);
    if (process.env.NODE_ENV !== 'production' &&
        process.env.NODE_ENV !== 'test') {
        if (!label) {
            throw new AccessibilityError('Pagination', 'The `label` prop is missing.');
        }
        if (!previousLabel) {
            throw new AccessibilityError('Pagination', 'The `previousLabel` prop is missing.');
        }
        if (!nextLabel) {
            throw new AccessibilityError('Pagination', 'The `nextLabel` prop is missing.');
        }
    }
    // Can't use our custom useClickEvent here because it doesn't allow us
    // to add the page number as label. So we implement it from scratch:
    const dispatch = useClickTrigger();
    const handleChange = (pageNumber) => {
        const { label: trackingLabel = pageNumber.toString(), component = 'pagination', customParameters, } = tracking;
        if (label) {
            dispatch({ label: trackingLabel, component, customParameters });
        }
        onChange(pageNumber);
    };
    if (!totalPages || totalPages < 2) {
        return null;
    }
    const showList = totalPages <= 5;
    const pages = PaginationService.generatePages(totalPages);
    return (_jsxs(Nav, Object.assign({ role: "navigation", "aria-label": label }, props, { children: [_jsx(IconButton, Object.assign({ label: previousLabel, size: "kilo", variant: "tertiary", disabled: currentPage <= 1, onClick: () => handleChange(currentPage - 1), css: prevButtonStyles }, { children: _jsx(ChevronLeft, { size: "16" }) })), showList ? (_jsx(PageList, { onChange: handleChange, pageLabel: pageLabel, pages: pages, currentPage: currentPage })) : (_jsx(PageSelect, { label: label, onChange: handleChange, pages: pages, currentPage: currentPage, totalPages: totalPages, totalLabel: totalLabel })), _jsx(IconButton, Object.assign({ label: nextLabel, size: "kilo", variant: "tertiary", disabled: currentPage >= totalPages, onClick: () => handleChange(currentPage + 1), css: nextButtonStyles }, { children: _jsx(ChevronRight, { size: "16" }) }))] })));
};
